import { useEffect, useState } from 'react';
import { useShopContext } from '../../../context/ShopContext';
import { Branch, Store, User, Theme, Responsible } from '../../../types';
import api from '../../../api/client';

interface FormErrors {
  name?: string[];
  store?: string[];
  manager?: string[];
  theme?: string[];
  responsibles?: string[];
}

interface BranchFormData {
  name: string;
  store?: number;
  manager?: number;
  theme?: number;
}

export default function BranchesTab() {
  const {
    adminBranches,
    stores : storesA,
    loading,
    error,
    managers,
    themes : themesA,
    fetchManagers,
    fetchAdminBranches,
    createBranch,
    updateBranch,
    deleteBranch,
  } = useShopContext();

  const [formVisible, setFormVisible] = useState(false);
  const [formData, setFormData] = useState<BranchFormData>({
    name: '',
    store: undefined,
    manager: undefined,
    theme: undefined,
  });
  const [editingBranchId, setEditingBranchId] = useState<number | null>(null);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [stores, setStores] = useState<Store[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [themes, setThemes] = useState<Theme[]>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(true);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        setIsLoadingOptions(true);
        const [storesRes,  themesRes] = await Promise.all([
          api.get<{ results: Store[] }>('/stores/'),
          api.get<{ results: Theme[] }>('/themes/'),
        ]);
        setStores(storesRes.data.results || []);
        setThemes(themesRes.data.results || []);
      } catch (err) {
        setFormErrors({ name: ['Failed to load form options'] });
      } finally {
        setIsLoadingOptions(false);
      }
    };

    fetchOptions();
    fetchAdminBranches();
    fetchManagers()
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ 
      ...prev, 
      [name]: name === 'store' || name === 'manager' || name === 'theme' 
        ? value ? parseInt(value) : undefined 
        : value 
    }));
    setFormErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleMultiSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOptions = Array.from(e.target.selectedOptions).map((option) =>
      parseInt(option.value)
    );
    setFormData((prev) => ({ ...prev, responsibles: selectedOptions }));
    setFormErrors((prev) => ({ ...prev, responsibles: undefined }));
  };

  const handleSubmit = async () => {
    if (!formData.name?.trim()) {
      setFormErrors({ name: ['Branch name is required'] });
      return;
    }
    if (!formData.store) {
      setFormErrors((prev) => ({ ...prev, store: ['Store is required'] }));
      return;
    }
    if (!formData.manager) {
      setFormErrors((prev) => ({ ...prev, manager: ['Manager is required'] }));
      return;
    }
    if (!formData.theme) {
      setFormErrors((prev) => ({ ...prev, theme: ['Theme is required'] }));
      return;
    }
  

    try {
      const storeObj = storesA.find(s => s.id === formData.store);
      const managerObj = managers.find(u => u.id === formData.manager);
      const themeObj = themesA.find(t => t.id === formData.theme);

      if (!storeObj || !managerObj || !themeObj) {
        throw new Error('Invalid form data');
      }

      const payload: Partial<Branch> = {
        name: formData.name,
        store: storeObj,
        manager: managerObj.user,
        theme: themeObj,
      };

      if (editingBranchId) {
        await updateBranch(editingBranchId, payload);
      } else {
        await createBranch(payload);
      }
      resetForm();
    } catch (err: any) {
      const errors = err.response?.data || { name: ['Failed to save branch'] };
      setFormErrors(errors);
    }
  };

  const resetForm = () => {
    setFormVisible(false);
    setFormData({ name: '', store: undefined, manager: undefined, theme: undefined,  });
    setEditingBranchId(null);
    setFormErrors({});
  };

  const handleEdit = (branch: Branch) => {
    setFormVisible(true);
    setEditingBranchId(branch.id);
    setFormData({
      name: branch.name,
      store: branch.store.id,
      manager: branch.manager?.id,
      theme: branch.theme?.id,
    });
  };

  const handleDelete = async (id: number) => {
    const confirmed = window.confirm('Are you sure you want to delete this branch?');
    if (confirmed) {
      await deleteBranch(id);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 relative">
      <h2 className="text-2xl font-bold mb-6 flex justify-between items-center">
        Branches
        <button
          onClick={() => setFormVisible(true)}
          className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
          aria-label="Add new branch"
        >
          + Add Branch
        </button>
      </h2>

      {loading && (
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-blue-500"></div>
        </div>
      )}
      {error && <p className="text-red-600 bg-red-50 p-3 rounded-lg mb-4">{error}</p>}
      {!loading && !error && adminBranches.length === 0 && (
        <p className="text-gray-600">No branches available.</p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {adminBranches.map((branch) => {
          const theme = branch.theme || branch.store?.theme;
          const logo = theme?.logo;

          return (
            <div
              key={branch.id}
              className="border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
              style={{
                borderColor: theme?.primary_color || '#e5e7eb',
              }}
            >
              {logo && (
                <img
                  src={logo}
                  alt={`${branch.name} logo`}
                  className="h-12 w-auto mb-4 rounded"
                  onError={(e) => (e.currentTarget.src = '/placeholder-logo.png')}
                />
              )}
              <h3 className="font-bold text-lg text-gray-800">{branch.name}</h3>
              <p className="text-sm text-gray-600 mb-1">
                <span className="font-medium">Store:</span>{' '}
                {branch.store?.name || 'N/A'}
              </p>
              <p className="text-sm text-gray-600 mb-1">
                <span className="font-medium">Manager:</span>{' '}
                {branch.manager?.username || 'N/A'}
              </p>
     
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => handleEdit(branch)}
                  className="text-blue-600 hover:underline text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
                  aria-label={`Edit ${branch.name}`}
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(branch.id)}
                  className="text-red-600 hover:underline text-sm font-medium focus:outline-none focus:ring-2 focus:ring-red-500"
                  aria-label={`Delete ${branch.name}`}
                >
                  Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {formVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-lg">
            <h3 className="text-xl font-semibold mb-6 text-gray-800">
              {editingBranchId ? 'Edit Branch' : 'Add New Branch'}
            </h3>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Branch Name *
                </label>
                <input
                  id="name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter branch name"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  aria-required="true"
                />
                {formErrors.name && (
                  <p className="text-red-600 text-sm mt-1">{formErrors.name[0]}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="store"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Store *
                </label>
                <select
                  id="store"
                  name="store"
                  value={formData.store?.toString() || ''}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  aria-required="true"
                  disabled={isLoadingOptions}
                >
                  <option value="">Select a store</option>
                  {storesA.map((store) => (
                    <option key={store.id} value={store.id}>
                      {store.name}
                    </option>
                  ))}
                </select>
                {formErrors.store && (
                  <p className="text-red-600 text-sm mt-1">{formErrors.store[0]}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="manager"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Manager *
                </label>
                <select
                  id="manager"
                  name="manager"
                  value={formData.manager?.toString() || ''}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  aria-required="true"
                  disabled={isLoadingOptions}
                >
                  <option value="">Select a manager</option>
                  {managers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name} ({user.role})
                    </option>
                  ))}
                </select>
                {formErrors.manager && (
                  <p className="text-red-600 text-sm mt-1">{formErrors.manager[0]}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="theme"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Theme *
                </label>
                <select
                  id="theme"
                  name="theme"
                  value={formData.theme?.toString() || ''}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  aria-required="true"
                  disabled={isLoadingOptions}
                >
                  <option value="">Select a theme</option>
                  {themesA.map((theme) => (
                    <option key={theme.id} value={theme.id}>
                      Theme {theme.id} ({theme.primary_color})
                    </option>
                  ))}
                </select>
                {formErrors.theme && (
                  <p className="text-red-600 text-sm mt-1">{formErrors.theme[0]}</p>
                )}
              </div>

            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={resetForm}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                aria-label="Cancel"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label={editingBranchId ? 'Update branch' : 'Create branch'}
                disabled={isLoadingOptions}
              >
                {editingBranchId ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}